import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Clock, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { track } from '@/lib/analytics';
import { cn, formatPrice } from '@/lib/utils';
import { searchApi } from '@/services/api/tracking';
import { useZoneSlug } from '@/stores/zone.store';
import { useReturnFocus } from '@/hooks/useReturnFocus';
import { useUiStore } from '@/stores/ui.store';

/**
 * ARAMA KATMANI
 *
 * Basit bir input değil: klavyeyle gezilebilen, anlık sonuç veren bir
 * komut paleti. Mobilde tam ekran, masaüstünde üstten inen panel.
 *
 * Erişilebilirlik: Radix Dialog focus trap ve Escape'i sağlar; sonuç
 * listesi ok tuşlarıyla gezilir, Enter seçer.
 */

const RECENT_KEY = 'hc.recentSearches';
const MAX_RECENT = 5;

const POPULAR = ['Kırmızı gül', 'Bugün teslim', 'Orkide', '1000 tl altı', 'Doğum günü'];

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function pushRecent(term: string): void {
  try {
    const next = [term, ...readRecent().filter((entry) => entry !== term)].slice(
      0,
      MAX_RECENT,
    );
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Depolama kapalıysa son aramalar tutulmaz; arama çalışmaya devam eder.
  }
}

export function SearchOverlay() {
  const overlay = useUiStore((state) => state.overlay);
  const closeOverlay = useUiStore((state) => state.closeOverlay);
  const navigate = useNavigate();
  const zone = useZoneSlug();

  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recent, setRecent] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const open = overlay === 'search';

  // Kapanışta odak, aramayı açan düğmeye geri döner.
  useReturnFocus(open);

  useEffect(() => {
    if (open) {
      setRecent(readRecent());
      setTerm('');
      setDebounced('');
      setActiveIndex(-1);
    }
  }, [open]);

  // Her tuşta istek atılmaz.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  const results = useQuery({
    queryKey: ['search', 'suggest', debounced, zone ?? 'default'],
    queryFn: () => searchApi.suggest(debounced, zone),
    enabled: debounced.length >= 2,
    staleTime: 60_000,
  });

  // Analitik: yalnızca kullanıcı yazmayı bıraktığında.
  useEffect(() => {
    if (debounced.length >= 2 && results.data) {
      track('search', { search_term: debounced });
    }
  }, [debounced, results.data]);

  const goToProduct = useCallback(
    (slug: string) => {
      pushRecent(debounced);
      closeOverlay();
      void navigate(`/urun/${slug}`);
    },
    [debounced, closeOverlay, navigate],
  );

  const goToResults = useCallback(
    (query: string) => {
      if (!query.trim()) {
        return;
      }

      pushRecent(query.trim());
      closeOverlay();
      void navigate(`/arama?q=${encodeURIComponent(query.trim())}`);
    },
    [closeOverlay, navigate],
  );

  const items = results.data?.items ?? [];

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, items.length - 1));
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, -1));
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (activeIndex >= 0 && items[activeIndex]) {
        goToProduct(items[activeIndex].slug);
      } else {
        goToResults(term);
      }
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && closeOverlay()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in" />

        <Dialog.Content
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          className={cn(
            'fixed inset-x-0 top-0 z-50 flex max-h-dvh flex-col bg-surface',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-top',
            'sm:inset-x-auto sm:left-1/2 sm:top-16 sm:w-full sm:max-w-xl',
            'sm:-translate-x-1/2 sm:rounded-lg sm:shadow-lift',
            'h-dvh sm:h-auto',
          )}
        >
          <Dialog.Title className="sr-only-focusable">Ürün ara</Dialog.Title>
          <Dialog.Description className="sr-only-focusable">
            Ürün adı, çiçek türü, renk veya bütçe yazabilirsiniz.
          </Dialog.Description>

          {/* Arama alanı */}
          <div className="flex items-center gap-3 border-b border-line px-4 py-3.5">
            <Search className="size-5 shrink-0 text-ink-subtle" aria-hidden />

            <input
              ref={inputRef}
              type="search"
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setActiveIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder="gül, orkide, doğum günü, 1000 tl…"
              aria-label="Ürün ara"
              aria-autocomplete="list"
              aria-controls="search-results"
              className="h-9 min-w-0 flex-1 bg-transparent text-base text-ink placeholder:text-ink-subtle focus:outline-none"
            />

            <Dialog.Close
              aria-label="Aramayı kapat"
              className="flex size-9 shrink-0 items-center justify-center rounded-sm text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
            >
              <X className="size-[18px]" aria-hidden />
            </Dialog.Close>
          </div>

          <div id="search-results" className="min-h-0 flex-1 overflow-y-auto">
            {debounced.length < 2 ? (
              <SearchIdle
                recent={recent}
                onPick={(value) => {
                  setTerm(value);
                }}
              />
            ) : results.isPending ? (
              <SuggestSkeleton />
            ) : results.isError ? (
              <p className="px-4 py-10 text-center text-sm text-ink-muted">
                Arama şu anda yapılamıyor. Lütfen tekrar deneyin.
              </p>
            ) : items.length === 0 ? (
              <EmptyResults term={debounced} />
            ) : (
              <>
                {results.data.parsed.hints.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 px-4 pt-3">
                    {results.data.parsed.hints.map((hint) => (
                      <Badge key={hint} tone="sameDay">
                        {hint}
                      </Badge>
                    ))}
                  </div>
                ) : null}

                <ul role="listbox" aria-label="Arama sonuçları" className="p-2">
                  {items.map((product, index) => (
                    <li key={product.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={index === activeIndex}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => goToProduct(product.slug)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left',
                          'transition-colors duration-fast',
                          index === activeIndex
                            ? 'bg-surface-sunken'
                            : 'hover:bg-surface-sunken',
                        )}
                      >
                        {product.images[0] ? (
                          <img
                            src={product.images[0].url}
                            alt=""
                            aria-hidden
                            loading="lazy"
                            className="size-12 shrink-0 rounded-sm bg-surface-sunken object-cover"
                          />
                        ) : (
                          <span className="size-12 shrink-0 rounded-sm bg-surface-sunken" />
                        )}

                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-ink">
                            {product.name}
                          </span>
                          <span className="mt-0.5 block text-xs text-ink-muted">
                            {product.deliveryHint === 'today'
                              ? 'Bugün teslim edilebilir'
                              : product.deliveryHint === 'soldOut'
                                ? 'Tükendi'
                                : 'Yarından itibaren'}
                          </span>
                        </span>

                        <span
                          className="shrink-0 text-sm text-ink"
                          data-price
                        >
                          {formatPrice(product.price)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => goToResults(term)}
                  className="flex w-full items-center justify-between gap-3 border-t border-line px-4 py-3.5 text-sm text-leaf-600 transition-colors hover:bg-leaf-50"
                >
                  Tüm sonuçları gör ({results.data.total})
                  <ArrowRight className="size-4" aria-hidden />
                </button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SearchIdle({
  recent,
  onPick,
}: {
  recent: string[];
  onPick: (term: string) => void;
}) {
  return (
    <div className="p-4">
      {recent.length > 0 ? (
        <section className="mb-6">
          <h3 className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-ink-subtle">
            <Clock className="size-3.5" aria-hidden />
            Son aramalarınız
          </h3>

          <ul className="mt-2.5 flex flex-wrap gap-2">
            {recent.map((entry) => (
              <li key={entry}>
                <button
                  type="button"
                  onClick={() => onPick(entry)}
                  className="rounded-pill border border-line px-3 py-1.5 text-sm text-ink-muted transition-colors hover:border-line-strong hover:text-ink"
                >
                  {entry}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section>
        <h3 className="text-xs uppercase tracking-wide text-ink-subtle">
          Sık aranan
        </h3>

        <ul className="mt-2.5 flex flex-wrap gap-2">
          {POPULAR.map((entry) => (
            <li key={entry}>
              <button
                type="button"
                onClick={() => onPick(entry)}
                className="rounded-pill border border-line px-3 py-1.5 text-sm text-ink-muted transition-colors hover:border-line-strong hover:text-ink"
              >
                {entry}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <p className="mt-6 text-xs text-ink-subtle">
        Bütçenizi de yazabilirsiniz: “1000 tl altı”, “500-1500 tl”.
      </p>
    </div>
  );
}

function EmptyResults({ term }: { term: string }) {
  return (
    <div className="px-6 py-12 text-center">
      <p className="font-serif text-lg text-ink">
        “{term}” için sonuç bulamadık
      </p>
      <p className="mt-2 text-sm text-ink-muted">
        Farklı bir kelime deneyin ya da bütçenizi yazın: “1000 tl altı”.
      </p>
    </div>
  );
}

function SuggestSkeleton() {
  return (
    <ul className="space-y-1 p-2">
      {Array.from({ length: 4 }, (_, index) => (
        <li key={index} className="flex items-center gap-3 px-2 py-2">
          <Skeleton className="size-12 shrink-0 rounded-sm" />
          <div className="flex-1">
            <Skeleton className="h-4 w-3/5" />
            <Skeleton className="mt-2 h-3 w-1/3" />
          </div>
          <Skeleton className="h-4 w-16" />
        </li>
      ))}
    </ul>
  );
}
